/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compact, map, sortBy, uniq } from 'lodash';
import { branch, compose } from 'recompose';

import connectToFluxibleContext from 'js/lib/connectToFluxibleContext';
import connectToRouter from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Grid } from '@coursera/cds-core';

import OtherTopics from 'bundles/browse-common/legacy/components/Domain/OtherTopics';
import withNotFound from 'bundles/common/components/withNotFound';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import { chooseFeaturedPartner } from 'bundles/partners-graphql/actions/PartnersActionCreators';
import CountryTags from 'bundles/partners-graphql/components/listing/CountryTags';
import PartnerBoxes from 'bundles/partners-graphql/components/listing/PartnerBoxes';
import PartnerListingHeader from 'bundles/partners-graphql/components/listing/PartnerListingHeader';
import CountryNameQuery from 'bundles/partners-graphql/queries/CountryNameQuery';
import type { GetCountryNameResponse } from 'bundles/partners-graphql/queries/CountryNameQuery';
import GetAllPartners from 'bundles/partners-graphql/queries/GetAllPartners.graphql';
import type {
  GetAllDisplayedPartnersQuery,
  GetAllDisplayedPartnersQueryVariables,
  PartnerListingPagePartnerFragment as PartnerListingPagePartner,
} from 'bundles/partners-graphql/queries/__generated__/GetAllPartners';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/partners-graphql';

// Setting a high limit for the partners listing page query as pagination is implemented in the UI.
// The current total number of partners is 364.
const PARTNERS_LISTING_PAGE_QUERY_LIMIT = 500;

const styles = {
  partnerListingPage: css`
    .partner-listing-body {
      max-width: 1024px;
    }

    .internal-linking-wrapper-cds {
      margin: 60px auto;
      margin-bottom: 30px;
      padding: 0 16px;

      .internal-linking-container-cds {
        max-width: 1114px;
        margin-left: auto;
        margin-right: auto;

        @media (min-width: 1440px) {
          margin: 45px auto;
        }

        a {
          &:hover {
            text-decoration: none;
            color: var(--cds-color-neutral-primary);
          }
        }
      }
    }
  `,
};

type PropsFromCaller = {
  countryCode: string;
};

type PropsFromPartnersQuery = {
  partners: Array<PartnerListingPagePartner>;
};

type PropsToComponent = PropsFromCaller & PropsFromPartnersQuery;

export const PartnerListingPage = ({ partners, countryCode }: PropsToComponent) => {
  const getCountryCodes = () => {
    const countryCodes = sortBy(
      uniq(compact(map(partners, (partner: PartnerListingPagePartner) => partner?.location?.country?.toUpperCase())))
    );

    return countryCodes;
  };

  // Removing Russia from country list
  const countryCodes = getCountryCodes().filter((code) => code.toLowerCase() !== 'ru');
  return (
    <div css={styles.partnerListingPage} className="styleguide" data-e2e="partner-listing-page">
      <PartnerListingHeader partners={partners} />
      <div className="partner-listing-body bt3-container" data-js="partnerListingBody">
        <CountryTags countryCodes={countryCodes} selectedCountry={countryCode} />
        {partners && <PartnerBoxes selectedCountry={countryCode} partners={partners} />}
      </div>
      <Grid container className="internal-linking-wrapper-cds">
        <Grid item xs={12} className="internal-linking-container-cds">
          <OtherTopics />
        </Grid>
      </Grid>
      <CourseraMetatags
        metaNameAndProperties={{
          title: _t('Partners'),
        }}
      />
    </div>
  );
};
PartnerListingPage.LOADING_COMPONENT = LoadingIcon;

export default compose<PropsToComponent, PropsFromCaller>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      page: 'partner_listing',
    },
  })),
  connectToFluxibleContext(({ executeAction }) => {
    executeAction(chooseFeaturedPartner);
  }),
  waitForGraphQL<
    PropsFromCaller,
    GetAllDisplayedPartnersQuery,
    GetAllDisplayedPartnersQueryVariables,
    PropsFromPartnersQuery
  >(GetAllPartners, {
    props: ({ data }) => {
      const partners = data?.Partner?.queryAll?.elements.filter(Boolean) || [];
      return { partners };
    },
    options: () => {
      return {
        variables: {
          limit: PARTNERS_LISTING_PAGE_QUERY_LIMIT,
        },
        context: {
          clientName: 'gatewayGql',
        },
      };
    },
  }),
  connectToRouter((params) => {
    return { countryCode: params.location.query.countryCode };
  }),
  withNotFound({
    test: ({ partners, countryCode }) => {
      // Removing access to Russia partners listing page from country list
      return !partners || partners?.length === 0 || countryCode === 'ru';
    },
  }),
  branch(
    ({ countryCode }: PropsToComponent) => {
      // Only make the graphql call if the countryCode param is present
      return !!countryCode;
    },
    compose<PropsToComponent, PropsFromCaller & PropsFromPartnersQuery>(
      waitForGraphQL(CountryNameQuery, {
        props: ({ data }: { data?: { GetCountryName?: GetCountryNameResponse } }) => {
          const country = data?.GetCountryName?.country;

          return {
            isInvalidCountryCodePresent: !country,
          };
        },
        options: ({ countryCode }: PropsFromCaller) => {
          return {
            variables: { countryNameQueryInput: { iso_alpha_2_code: countryCode } },
          };
        },
      }),
      withNotFound({
        test: ({ partners, isInvalidCountryCodePresent, countryCode }) => {
          const countryCodesFromPartners = sortBy(
            uniq(
              compact(
                map(
                  partners,
                  (partner: PartnerListingPagePartner) =>
                    partner?.location?.country && partner?.location?.country.toLowerCase()
                )
              )
            )
          );
          const hasNoPartnersForCountryCode = !(
            countryCodesFromPartners && countryCodesFromPartners.includes(countryCode)
          );

          return hasNoPartnersForCountryCode || isInvalidCountryCodePresent;
        },
      })
    )
  )
)(PartnerListingPage);
